import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Grid, Box, CircularProgress, Alert, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { swarmApi } from '../api';

function Monitoring({ observabilityStatus }) {
  const [services, setServices] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sRes, nRes, tRes, iRes] = await Promise.all([
        swarmApi.listServices().catch(() => ({ data: [] })),
        swarmApi.listNodes().catch(() => ({ data: [] })),
        swarmApi.listTasks().catch(() => ({ data: [] })),
        swarmApi.getInfo().catch(() => ({ data: null })),
      ]);
      setServices(sRes.data);
      setNodes(nRes.data);
      setTasks(tRes.data);
      setInfo(iRes.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const runningServices = services.filter(s => s.status === 'running').length;
  const degradedServices = services.filter(s => s.status === 'degraded').length;
  const stoppedServices = services.filter(s => s.status === 'stopped').length;

  if (loading && services.length === 0) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Monitoring</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
      </Box>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="success.main" fontWeight="bold">{runningServices}</Typography>
            <Typography variant="subtitle2">Services Running</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="warning.main" fontWeight="bold">{degradedServices}</Typography>
            <Typography variant="subtitle2">Degraded</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="error.main" fontWeight="bold">{stoppedServices}</Typography>
            <Typography variant="subtitle2">Stopped</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="info.main" fontWeight="bold">{nodes.length || '-'}</Typography>
            <Typography variant="subtitle2">Nodes</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Service Health</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Replicas</TableCell>
                    <TableCell>Image</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map(s => (
                    <TableRow key={s.id}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>
                        <Chip label={s.status} size="small"
                          color={s.status === 'running' ? 'success' : s.status === 'degraded' ? 'warning' : 'error'} />
                      </TableCell>
                      <TableCell>{s.runningReplicas}/{s.desiredReplicas}</TableCell>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.image}</TableCell>
                    </TableRow>
                  ))}
                  {services.length === 0 && (
                    <TableRow><TableCell colSpan={4}><Typography color="text.secondary">No services found</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Swarm Nodes</Typography>
            {nodes.length > 0 ? (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Hostname</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Engine</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nodes.map(n => (
                      <TableRow key={n.id}>
                        <TableCell>{n.hostname}</TableCell>
                        <TableCell><Chip label={n.role} size="small" color={n.role === 'manager' ? 'primary' : 'default'} /></TableCell>
                        <TableCell><Chip label={n.state} size="small" color={n.state === 'ready' ? 'success' : 'warning'} /></TableCell>
                        <TableCell>{n.engineVersion}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">Swarm not active or no nodes found</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tasks Overview */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Recent Tasks ({tasks.length})</Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.slice(0, 50).map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{t.serviceName || t.serviceID?.substring(0, 12)}</TableCell>
                    <TableCell>
                      <Chip label={t.state} size="small"
                        color={t.state === 'running' ? 'success' : t.state === 'complete' ? 'info' : t.state === 'failed' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.image}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.message}</TableCell>
                    <TableCell>{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : '-'}</TableCell>
                  </TableRow>
                ))}
                {tasks.length === 0 && (
                  <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No tasks found</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Grafana Dashboards</Typography>
          {observabilityStatus?.grafanaOnline ? (
            <Box
              component="iframe"
              src="/grafana/"
              sx={{
                width: '100%',
                height: 500,
                border: 'none',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
              title="Grafana Swarm Dashboard"
            />
          ) : (
            <Box sx={{
              width: '100%',
              p: 4,
              border: '1px dashed',
              borderColor: 'warning.main',
              borderRadius: 1,
              bgcolor: 'action.hover',
              textAlign: 'center',
            }}>
              <Typography variant="h6" color="warning.main" fontWeight="bold" gutterBottom>
                Observability Stack Offline
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                The Grafana dashboard container is currently not online in the swarm. To enable real-time charts and metrics visualization, deploy the monitoring stack services.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
export default Monitoring;
